import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { ArrowRight } from "@phosphor-icons/react";

const region = () => {
  const lang = (navigator.language || "").split("@")[0];
  try {
    return (new Intl.Locale(lang).region || lang.split("-")[1] || "").toUpperCase();
  } catch {
    return (lang.split("-")[1] || "").toUpperCase();
  }
};
const IMPERIAL_REGIONS = ["US", "LR", "MM"];

const cmToFtIn = (cm) => {
  const totalIn = Math.round(cm / 2.54);
  return `${Math.floor(totalIn / 12)}'${totalIn % 12}"`;
};
const kgToLb = (kg) => Math.round(kg * 2.20462);

export default function HeightWeightPicker({ onConfirm, disabled }) {
  const [metric, setMetric] = useState(() => !IMPERIAL_REGIONS.includes(region()));
  const [heightCm, setHeightCm] = useState(172);
  const [weightKg, setWeightKg] = useState(75);

  const heightLabel = metric ? `${heightCm} cm` : cmToFtIn(heightCm);
  const weightLabel = metric ? `${weightKg} kg` : `${kgToLb(weightKg)} lb`;

  const confirm = () =>
    onConfirm(metric ? `${heightCm} cm, ${weightKg} kg` : `${cmToFtIn(heightCm)}, ${kgToLb(weightKg)} lb`);

  return (
    <div className="bg-surface border border-border rounded-3xl p-5 shadow-sm max-w-md ml-auto" data-testid="height-weight-picker">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Slide to your height & weight</span>
        <div className="flex rounded-full bg-secondary p-0.5 text-xs font-semibold">
          <button
            data-testid="unit-metric-btn"
            onClick={() => setMetric(true)}
            className={`px-3 py-1 rounded-full transition-colors ${metric ? "bg-brand text-white" : "text-muted2"}`}
          >
            cm / kg
          </button>
          <button
            data-testid="unit-imperial-btn"
            onClick={() => setMetric(false)}
            className={`px-3 py-1 rounded-full transition-colors ${!metric ? "bg-brand text-white" : "text-muted2"}`}
          >
            ft / lb
          </button>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-muted2 uppercase tracking-wide font-medium">Height</span>
          <span className="font-display text-xl" data-testid="height-value">{heightLabel}</span>
        </div>
        <Slider
          data-testid="height-slider"
          value={[heightCm]}
          min={130}
          max={220}
          step={1}
          onValueChange={([v]) => setHeightCm(v)}
          className="mt-2"
        />
      </div>

      <div className="mt-5">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-muted2 uppercase tracking-wide font-medium">Weight</span>
          <span className="font-display text-xl" data-testid="weight-value">{weightLabel}</span>
        </div>
        <Slider
          data-testid="weight-slider"
          value={[weightKg]}
          min={40}
          max={180}
          step={1}
          onValueChange={([v]) => setWeightKg(v)}
          className="mt-2"
        />
      </div>

      <button
        data-testid="height-weight-confirm-btn"
        onClick={confirm}
        disabled={disabled}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50"
      >
        That's me <ArrowRight size={15} weight="bold" />
      </button>
    </div>
  );
}
